import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CompositionButtons, { CancelButton } from './composition_buttons';
import createCompositionForm from './lib/create_composition_form';
import { createErrorDisplayMixin } from 'components/lib/error_display_mixin';
import { FormInputSectionTypes, FormSectionTypes } from 'models/external_action_form';
import Err from 'models/err';
import ExternalActionForm from 'models/external_action_form';
import ExternalFormCompositionContent from 'models/composition/external_form_composition_content';
import PerformAction from 'actions/external_customer_lookup/perform_action';
import PerformExternalAction from 'models/perform_external_action';
import scrollLock from 'components/lib/util/scroll_lock_handler';
import SendButton from './send_button';
import Section from './external_form/external_form_section';
import Spinner from 'components/common/spinner';

const ExternalFormForm = createCompositionForm({
  mixins: [createErrorDisplayMixin('compositionErrors')],

  propTypes: {
    externalActionForm: PropTypes.instanceOf(ExternalActionForm).isRequired,
    isWaitingOnAction: PropTypes.bool,
    customerId: PropTypes.string.isRequired,
  },

  getInitialState() {
    const sections = _.get(this.props.externalActionForm, 'sections', []);
    const initialFormState = _.reduce(
      sections,
      (result, section) => {
        if (section.type === FormSectionTypes.input) {
          result[section.attr] = section.defaultValue || '';
        }
        return result;
      },
      {}
    );

    return {
      editorState: this.getNewEditorState(),
      errors: this.props.errors,
      sectionsContent: initialFormState,
    };
  },

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ errors: nextProps.errors });
  },

  getCompositionContent() {
    return new ExternalFormCompositionContent({
      errors: this.state.errors,
      sectionsContent: this.state.sectionsContent,
    });
  },

  loadComposition(composition) {
    const errors = composition.content.errors || this.state.errors;
    const sectionsContent = composition.content.sectionsContent || this.state.sectionsContent;
    this.setState({
      errors,
      sectionsContent,
    });
  },

  /* Handlers */

  onSubmit() {
    if (this.props.isWaitingOnAction) {
      return;
    }

    let actionUrl = _.get(this.props.externalActionForm, 'actionUrl');
    let customerId = this.props.customerId;

    let errors = this.validateForm();
    if (errors) {
      this.setState({ errors });
      return;
    }

    let sections = this.state.sectionsContent;
    if (sections.amount) {
      sections.amount = _.toNumber(sections.amount);
    }

    const payload = PerformExternalAction.create({
      customerId,
      action: {
        actionUrl,
        ...sections,
      },
    });

    this.executeAction(PerformAction, payload);
  },

  validateForm() {
    let errors = [];

    // ignore checkbox inputs since they should always be optional
    this.props.externalActionForm.sections
      .filter(s => s.type === 'input' && s.input.type !== FormInputSectionTypes.checkbox && !s.input.optional)
      .forEach(s => {
        const content = _.get(this.state.sectionsContent, s.attr);
        if (content === '') {
          errors.push(new Err({ attr: s.attr, code: Err.INVALID, detail: 'Please enter a value.' }));
        }

        if (s.attr === 'amount') {
          let amount = _.toNumber(this.state.sectionsContent.amount);
          if (!_.isFinite(amount)) {
            errors.push(new Err({ attr: s.attr, code: Err.INVALID, detail: 'Please enter the amount to refund.' }));
          } else {
            const splitAmount = _.toString(amount).split('.');
            if (splitAmount.length > 1 && splitAmount[1].length > 2) {
              errors.push(new Err({ attr: s.attr, code: Err.INVALID, detail: 'Please enter a valid amount.' }));
            } else if (amount < 0) {
              errors.push(new Err({ attr: s.attr, code: Err.INVALID, detail: 'Please enter a positive value.' }));
            } else if (s.input.max && amount > _.toNumber(s.input.max)) {
              errors.push(
                new Err({ attr: s.attr, code: Err.INVALID, detail: `Please enter an amount less than ${s.input.max}.` })
              );
            }
          }
        }
      });

    return errors.length === 0 ? null : errors;
  },

  onUpdateField(newAttributes) {
    const newFormState = { ...this.state.sectionsContent, ...newAttributes };
    this.setState({
      sectionsContent: newFormState,
    });
  },

  /* Actions */

  onTryCancel(evt) {
    if (this.props.isWaitingOnAction) {
      return _.noop();
    }

    return this.onCancel(evt);
  },

  /* Render */

  render() {
    let sections = _.get(this.props.externalActionForm, 'sections', []);
    let submitLabel = _.get(this.props.externalActionForm, 'submitButton', 'Submit');

    return (
      <div className="taskCompositionForm compositionForm">
        <div className="compositionForm-header taskCompositionForm-header">
          <div className="compositionForm-header-text taskCompositionForm-header-text">
            {_.get(this.props.externalActionForm, 'title')}
          </div>
        </div>

        <div className="compositionForm-scrollContainer" onWheel={scrollLock} ref={node => (this.bodyWrapper = node)}>
          <div className="compositionForm-contentContainer">
            <div className="compositionForm-bodyContainer">
              {sections.length > 0 && _.map(sections, section => this.renderSection(section))}
            </div>
            {this.renderErrors('compositionForm-error')}
          </div>
        </div>

        <CompositionButtons>
          <CancelButton
            onClick={this.onTryCancel}
            label={_.get(this.props.externalActionForm, 'closeButton', 'Close')}
          />
          <SendButton disabled={this.props.isMinimized} onClick={this.onSubmit}>
            {this.renderLoadingSpinner(this.props.isWaitingOnAction, submitLabel)}
          </SendButton>
        </CompositionButtons>
      </div>
    );
  },

  renderLoadingSpinner(isWaitingOnAction, label) {
    if (!isWaitingOnAction) {
      return label;
    }

    return (
      <StyledSpinnerContainer>
        <Spinner color="FFF" stroke={1} />
      </StyledSpinnerContainer>
    );
  },

  renderSection(section) {
    let error = this.state.errors.find(e => e.attr === section.attr);
    const errorMessage = error ? error.detail : '';
    return (
      <Section
        key={`section-${section.attr}`}
        section={section}
        updateState={this.onUpdateField}
        value={_.get(this.state.sectionsContent, section.attr, null)}
        error={errorMessage}
      />
    );
  },
});

const StyledSpinnerContainer = styled.div`
  width: 16px;
  display: inline-block;
`;

export default ExternalFormForm;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/composition/external_form_form.jsx